import AssignmentsServiceClient from "@seneca/assignments-service-client";
import {
  EnrichedTeacherClassAssignmentWithContentAssignees,
  FetchExamQuestionAnswersResponse
} from "@seneca/assignments-service-types";
import { ActiveUsers } from "@seneca/school-analytics-service-types";
import {
  FetchSchoolQuizOverviewsResponse,
  FetchTeacherQuizOverviewsResponse,
  QuizModuleI,
  QuizOverviewI,
  RequestGenerateQuiz,
  TeacherQuizI
} from "@seneca/user-quiz-service-types";

import { getClient } from "services";

import { QuerySchoolQuizzesArgs, QueryTeacherQuizzesArgs } from "./queries";
import {
  CreateAssignmentPayloadType,
  UpdateAssignmentPayloadType
} from "./types";

export async function fetchClassAssignment<
  T extends EnrichedTeacherClassAssignmentWithContentAssignees
>({ classId, assignmentId }: { classId: string; assignmentId: string }) {
  return (await getClient("assignmentsService").fetchClassAssignment(
    classId,
    assignmentId
  )) as T;
}

export async function fetchAssignment(assignmentId: string) {
  return await getClient("assignmentsService").fetchAssignment(assignmentId);
}

export async function fetchClassAssignments({
  classId,
  date,
  limit
}: {
  classId: string;
  date?: string;
  limit: number;
}) {
  return await getClient("assignmentsService").fetchTeacherAssignmentsForClass(
    classId,
    date,
    limit
  );
}

export async function fetchAssignmentExamQuestionAnswers({
  classId,
  assignmentId
}: {
  classId: string;
  assignmentId: string;
}): Promise<FetchExamQuestionAnswersResponse> {
  return await getClient("assignmentsService").fetchExamQuestionAnswers(
    classId,
    assignmentId
  );
}

export async function updateClassAssignment(
  payload: UpdateAssignmentPayloadType
) {
  return await getClient("assignmentsService").updateAssignment(payload);
}

export async function createClassAssignment(
  payload: CreateAssignmentPayloadType
) {
  return await getClient("assignmentsService").createAssignment(payload);
}

export async function deleteClassAssignment({
  classId,
  assignmentId
}: {
  classId: string;
  assignmentId: string;
}) {
  return await getClient("assignmentsService").deleteAssignment(
    classId,
    assignmentId
  );
}

export async function fetchAllTeacherClassAssignments({
  from,
  to,
  schoolId,
  withAssignees
}: Parameters<AssignmentsServiceClient["fetchAllTeacherClassAssignments"]>[0]) {
  return await getClient("assignmentsService").fetchAllTeacherClassAssignments({
    from,
    to,
    schoolId,
    withAssignees
  });
}

export async function fetchTeacherAssignments(from?: string, to?: string) {
  return await getClient("assignmentsService").fetchTeacherAssignments({
    from,
    to
  });
}

export async function fetchTeacherQuiz(quizId?: string) {
  if (!quizId) return;

  return await getClient("userQuizService").fetchTeacherQuiz(quizId);
}

export async function queryTeacherQuizOverviews(
  queryArgs: QueryTeacherQuizzesArgs
): Promise<FetchTeacherQuizOverviewsResponse> {
  return await getClient("userQuizService").queryTeacherQuizOverviews(
    queryArgs
  );
}

export async function querySchoolTeacherQuizOverviews(
  schoolId: string,
  queryArgs: QuerySchoolQuizzesArgs
): Promise<FetchSchoolQuizOverviewsResponse> {
  return await getClient("userQuizService").querySchoolQuizOverviews({
    schoolId,
    ...queryArgs
  });
}

export async function requestGenerateQuiz(payload: RequestGenerateQuiz) {
  return await getClient("userQuizService").requestGenerateQuiz(payload);
}

export type UpdateTeacherQuizPayload = {
  quizId: string;
  quizOverview: Partial<QuizOverviewI>;
  contents?: {
    contentId: string;
    modules: (Partial<QuizModuleI> &
      Pick<QuizModuleI, "moduleType" | "content" | "id">)[];
  }[];
};

export async function updateTeacherQuiz(payload: UpdateTeacherQuizPayload) {
  return await getClient("userQuizService").updateTeacherQuiz(payload);
}

export async function copyQuiz(quizId: string) {
  return await getClient("userQuizService").copyQuiz(quizId);
}

export async function saveQuiz(quizId: string) {
  return await getClient("userQuizService").saveQuiz(quizId);
}

export async function fetchActiveUsersBySchool({
  from,
  to,
  schoolId
}: {
  from: string;
  to: string;
  schoolId: string;
}) {
  return (await getClient("schoolAnalyticsService").fetchActiveUsersBySchool({
    from,
    to,
    schoolId
  })) as ActiveUsers;
}

export async function fetchSchoolConnectionInfo(
  schoolId: string,
  startDate: string
) {
  return await getClient("schoolsService").fetchSchoolConnectionInfo(
    schoolId,
    startDate
  );
}

export async function fetchStudentQuiz(quizId: string): Promise<TeacherQuizI> {
  return await getClient("userQuizService").fetchStudyQuiz(quizId);
}

export type UserSectionStatsGraphQlArgs = {
  courseId?: string | null;
  sectionIds?: string[];
  userIds?: string[];
  fields?: string[];
};

export async function fetchClassesSectionStats({
  courseId,
  schoolId,
  classIds,
  sectionIds,
  bestScoreThreshold
}: {
  courseId: string;
  schoolId: string;
  classIds: string[];
  sectionIds: string[];
  bestScoreThreshold: number;
}) {
  return getClient("classService").fetchClassesSectionStats({
    courseId,
    schoolId,
    classIds,
    sectionIds,
    bestScoreThreshold
  });
}

export async function fetchEnrichedAggregatedUserSectionStats({
  userIds,
  sectionId,
  schoolId,
  courseId
}: {
  userIds: string[];
  schoolId: string;
  sectionId: string;
  courseId: string;
}) {
  return getClient("statsService").fetchAggregatedEnrichedUserSectionStats({
    userIds,
    sectionId,
    schoolId,
    courseId
  });
}

export async function fetchUserSectionStats(
  config: UserSectionStatsGraphQlArgs
) {
  return await getClient("statsService").graphQL(buildGraphQlArgs(config));
}

function buildGraphQlArgs({
  courseId,
  sectionIds = [],
  userIds = [],
  fields = ["courseId", "userId", "sectionIds", "timeFinished", "averageScore"]
}: UserSectionStatsGraphQlArgs) {
  return `
      {
        getSessionStatsOfCourseFromSectionIdsByUserIds(
          courseId: "${courseId}",
          sectionIds: [${sectionIds.map((id: any) => `"${id}"`).join(", ")}],
          userIds: [${userIds.map((id: any) => `"${id}"`).join(", ")}]) {
            ${fields.join("\n")}
        }
      }
    `;
}

export async function createUploadUrl(fileName: string, contentType: string) {
  return await getClient("userQuizService").createUploadUrl(
    fileName,
    contentType
  );
}

export async function uploadFileToS3({
  file,
  url,
  fields
}: {
  file: File;
  url: string;
  fields: { [key: string]: any };
}) {
  const formData = new FormData();
  Object.keys(fields).forEach(key => {
    formData.append(key, fields[key]);
  });
  formData.append("file", file);

  const response = await fetch(url, {
    method: "POST",
    body: formData
  });

  if (response.status !== 204) {
    const errorText = await response.text();
    throw new Error(errorText);
  }
}

export async function fetchFullSchoolClassesTeacher(
  schoolId: string | undefined
) {
  if (!schoolId) return [];

  const getSchoolServiceClient = () => getClient("schoolsService");
  return await getSchoolServiceClient().getSchoolClassesTeacher(schoolId, {
    limit: 5000,
    fullClasses: true
  });
}

export async function fetchSchoolClassEntitiesTeacher(
  schoolId: string | undefined
) {
  if (!schoolId) return [];

  const getSchoolServiceClient = () => getClient("schoolsService");
  return await getSchoolServiceClient().getSchoolClassesTeacher(schoolId, {
    limit: 5000,
    fullClasses: false
  });
}

export function queryEnrichedCourseSections(args: {
  courseId?: string | null;
  sectionId?: string;
  assignmentId?: string | null;
}) {
  return getClient("shortAnswerService").queryEnrichedCourseSections(args);
}
